import { useState, useEffect } from "react";
import { AlertTriangle, Eye, EyeOff, Lock, Mail } from "lucide-react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAdminAuth } from "../services/admin/AdminAuthServices";
import { useNotification } from "../context/NotificationContext";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // ─── Resolve redirect target ───────────────────────────────────────────────
  // Priority: ?next= query param (covers both apiFactory 401 hard-redirect AND
  // AdminProtectedRoutes soft-redirect) > location.state.from > dashboard
  const searchParams = new URLSearchParams(location.search);
  const nextParam = searchParams.get("next") || null;
  const reason = searchParams.get("reason") || null;
  const isSessionExpired = reason === "session_expired";

  // Resolve the destination after login
  const resolvedRedirect =
    nextParam ||
    (location.state?.from
      ? `${location.state.from.pathname}${location.state.from.search || ""}`
      : null) ||
    "/admin/dashboard/";

  const { adminLogin, loading, error, clearError } = useAdminAuth();
  const { showNotification } = useNotification();

  useEffect(() => {
    clearError();
    window.scrollTo(0, 0);

    // Fire the notification toast if apiFactory set the session-expired flag.
    // sessionStorage survives the hard-redirect but is cleared here so the
    // toast only appears once (not again on F5).
    try {
      if (sessionStorage.getItem("__session_expired") === "1") {
        sessionStorage.removeItem("__session_expired");
        showNotification({
          type: "warning",
          title: "Session Expired",
          message: "Your session has expired. Please sign in to continue.",
          duration: 6000,
        });
      }
    } catch (_) { }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      alert("All fields are required.");
      return;
    }

    const response = await adminLogin(email, password);

    if (response?.status === "success") {
      const { has_changed_password, last_login } = response.admin || {};

      if (has_changed_password === false) {
        // Enforce password change
        if (!last_login) {
          // First time login
          showNotification({
            type: "success",
            title: "Welcome aboard!",
            message: "We're glad to have you. For your security, please update your default password to continue.",
            duration: 8000
          });
        } else {
          // Returning user still on default password
          showNotification({
            type: "warning",
            title: "Security Action Required",
            message: "You are still using a default password. Please update it now to secure your account.",
            duration: 8000
          });
        }

        // Redirect to change-password regardless of where they were going
        navigate("/admin/change-password/", { replace: true });
      } else {
        // Normal flow
        navigate(resolvedRedirect, { replace: true });
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F3FF] via-white to-[#FAF5FF] dark:from-[#0A0A0A] dark:via-[#111111] dark:to-[#1A1A1A] flex items-center justify-center p-4">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#62397f]/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#7C3AED]/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="w-full max-w-md relative">
        {/* Logo and Brand */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="w-16 h-16 bg-gradient-to-br from-[#62397f] to-[#7C3AED]rounded-2xl flex items-center justify-center shadow-xl mx-auto mb-4 transform hover:scale-105 transition-transform duration-300">
            <img src="/static/images/logo.webp" alt="St. Stephen's Rentals Logo" loading="eager" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#111111] dark:text-white mb-2 font-poppins">
            Admin Login
          </h1>
          <p className="text-[#666666] dark:text-[#A0A0A0] font-inter">
            St. Stephen's Rentals &amp; Events
          </p>
        </div>

        {/* ── Session Expired Banner ── */}
        {isSessionExpired && (
          <div className="mb-4 flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl px-4 py-3 animate-fade-in">
            <AlertTriangle
              className="text-amber-500 dark:text-amber-400 mt-0.5 shrink-0"
              size={18}
            />
            <div>
              <p className="text-sm font-semibold text-amber-700 dark:text-amber-300 font-poppins">
                Session Expired
              </p>
              <p className="text-sm text-amber-600 dark:text-amber-400 font-inter">
                Your session has expired. Please sign in to continue.
              </p>
            </div>
          </div>
        )}

        {/* Login Card */}
        <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl shadow-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] p-6 md:p-8 transform hover:scale-[1.01] transition-transform duration-300">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* General Error Message (login failure, not session expiry) */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 animate-fade-in">
                <p className="text-red-600 dark:text-red-400 text-sm font-inter">
                  {error}
                </p>
              </div>
            )}

            {/* Email Field */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-[#111111] dark:text-white font-inter">
                Email Address
              </label>
              <div className="relative">
                <Mail
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#666666] dark:text-[#A0A0A0]"
                  size={18}
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl text-[#111111] dark:text-white placeholder-[#999999] dark:placeholder-[#666666] font-inter focus:outline-none focus:ring-2 focus:ring-[#62397f] focus:border-transparent transition-all duration-200"
                  placeholder="admin@ststephensrentals.com"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-[#111111] dark:text-white font-inter">
                Password
              </label>
              <div className="relative">
                <Lock
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#666666] dark:text-[#A0A0A0]"
                  size={18}
                />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl text-[#111111] dark:text-white placeholder-[#999999] dark:placeholder-[#666666] font-inter focus:outline-none focus:ring-2 focus:ring-[#62397f] focus:border-transparent transition-all duration-200"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[#666666] dark:text-[#A0A0A0] hover:text-[#62397f] dark:hover:text-[#A78BFA] transition-colors duration-200"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-[#E5E7EB] dark:border-[#2A2A2A] text-[#62397f] focus:ring-[#62397f]"
                />
                <span className="text-sm text-[#666666] dark:text-[#A0A0A0] font-inter">
                  Remember me
                </span>
              </label>
              <Link
                to="/admin/forgot-password"
                className="text-sm text-[#62397f] dark:text-[#A78BFA] hover:underline font-inter font-medium"
              >
                Forgot Password?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-6 bg-gradient-to-r from-[#62397f] to-[#7C3AED] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 font-poppins disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Signing in...
                </span>
              ) : (
                "Sign In"
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-[#666666] dark:text-[#A0A0A0] mt-6 font-inter">
          &copy; {new Date().getFullYear()} St. Stephen's Rentals &amp; Events.<br />All rights reserved.
        </p>
      </div>

      <style jsx="true" global="true">{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        .delay-1000 {
          animation-delay: 1s;
        }
      `}</style>
    </div>
  );
}
